/**
 * Simplified Stripe Service for DPEWizard - VERSION CORRIGÉE AVEC DEBUG
 */

import { supabase } from '@/utils/supabase';

export interface CreateCheckoutSessionRequest {
  priceId: string;
  creditsAmount?: number;
  mode: 'subscription' | 'payment';
  successUrl: string;
  cancelUrl: string;
  promoCode?: string;
  dpeId?: string;
  // ✅ NOUVEAU : Données utilisateur optionnelles
  customerEmail?: string;
  customerName?: string;
  userId?: string;
}

export interface CreateCheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export class StripeServiceError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode: number = 400,
    public details?: any
  ) {
    super(message);
    this.name = 'StripeServiceError';
  }
}

export class StripeService {
  async createCheckoutSession(request: CreateCheckoutSessionRequest): Promise<CreateCheckoutSessionResponse> {
    try {
      console.log('🔍 [STRIPE_SERVICE] createCheckoutSession called with request:', {
        mode: request.mode,
        priceId: request.priceId,
        creditsAmount: request.creditsAmount
      });
      
      console.log('🔍 [DEBUG] Starting checkout session creation:', {
        mode: request.mode,
        promoCode: request.promoCode,
        creditsAmount: request.creditsAmount,
        priceId: request.priceId
      });

      // ✅ NOUVEAU : Authentification optionnelle pour utilisateurs anonymes
      console.log('🔍 [STRIPE_SERVICE] Getting Supabase session...');
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      console.log('🔍 [STRIPE_SERVICE] Supabase session result:', {
        hasSession: !!session,
        hasError: !!sessionError,
        errorMessage: sessionError?.message
      });
      const authToken = session?.access_token || null;
      const isAuthenticated = !!authToken;
      
      console.log('🔍 [DEBUG] Auth status:', { isAuthenticated, hasToken: !!authToken });

      // Use Netlify function to create a Stripe session
      const endpoint = request.mode === 'subscription' 
        ? 'payment-create-subscription'
        : 'payment-one-time-payment';
      
      console.log('🔍 [STRIPE_SERVICE] Using endpoint:', endpoint);
      console.log('📡 [DEBUG] Using endpoint:', endpoint);

      // Send appropriate data based on mode
      const body = request.mode === 'subscription'
        ? {
            plan_id: 'monthly_unlimited', // ID du plan
            price_id: request.priceId,
            success_url: request.successUrl,
            cancel_url: request.cancelUrl,
            promo_code: request.promoCode || undefined
          }
        : {
            credits_amount: request.creditsAmount || 1,
            success_url: request.successUrl,
            cancel_url: request.cancelUrl,
            promo_code: request.promoCode || undefined,
            dpe_id: request.dpeId
          };

      console.log('📤 [DEBUG] Sending request body:', JSON.stringify(body, null, 2));
      console.log('🔍 [STRIPE_SERVICE] Request body prepared, about to make fetch call...');

      // ✅ NOUVEAU : Headers conditionnels selon authentification
      const headers = {
        'Content-Type': 'application/json',
        ...(authToken && { 'Authorization': `Bearer ${authToken}` })
      };

      console.log('🔍 [STRIPE_SERVICE] Headers prepared:', {
        hasAuth: !!authToken,
        contentType: headers['Content-Type']
      });
      
      console.log('🔍 [STRIPE_SERVICE] About to make fetch request to:', `/.netlify/functions/${endpoint}`);
      const response = await fetch(`/.netlify/functions/${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      console.log('🔍 [STRIPE_SERVICE] Fetch request completed, response status:', response.status);

      console.log('📥 [DEBUG] Response status:', response.status, response.statusText);

      if (!response.ok) {
        console.error('❌ [STRIPE_SERVICE] Response not OK, status:', response.status);
        let errorData;
        try {
          errorData = await response.json();
          console.error('❌ [DEBUG] Error response data:', JSON.stringify(errorData, null, 2));
        } catch (parseError) {
          console.error('❌ [DEBUG] Failed to parse error response:', parseError);
          errorData = { error: 'Failed to parse error response' };
        }

        // Messages d'erreur spécifiques pour les codes promo
        let errorMessage = errorData.error || 'Failed to create checkout session';
        let errorCode = 'CHECKOUT_CREATION_FAILED';

        if (errorData.error && typeof errorData.error === 'string') {
          // Détection erreurs codes promo courantes
          if (errorData.error.toLowerCase().includes('coupon') || 
              errorData.error.toLowerCase().includes('promo') ||
              errorData.error.toLowerCase().includes('discount')) {
            errorCode = 'PROMO_CODE_ERROR';
            errorMessage = `Code promo invalide: ${errorData.error}`;
          }
        }

        throw new StripeServiceError(
          errorMessage,
          errorCode,
          response.status,
          errorData
        );
      }

      console.log('🔍 [STRIPE_SERVICE] Response OK, parsing JSON...');
      let responseData;
      try {
        responseData = await response.json();
        console.log('🔍 [STRIPE_SERVICE] JSON parsed successfully');
        console.log('✅ [DEBUG] Success response data:', JSON.stringify(responseData, null, 2));
      } catch (parseError) {
        console.error('❌ [STRIPE_SERVICE] Failed to parse JSON response:', parseError);
        console.error('❌ [DEBUG] Failed to parse success response:', parseError);
        throw new StripeServiceError(
          'Invalid response from payment service',
          'INVALID_RESPONSE',
          500
        );
      }

      console.log('🔍 [STRIPE_SERVICE] Preparing result object...');
      const result = {
        sessionId: responseData.session_id || responseData.sessionId || '',
        url: responseData.checkout_url || responseData.url || '',
      };

      console.log('🔍 [STRIPE_SERVICE] Result prepared:', result);
      console.log('🎉 [DEBUG] Checkout session created successfully:', result);
      return result;

    } catch (error) {
      console.error('❌ [STRIPE_SERVICE] Error caught in createCheckoutSession:', error);
      console.error('💥 [DEBUG] Checkout session creation failed:', error);
      
      if (error instanceof StripeServiceError) {
        throw error;
      }
      
      // Erreur inattendue
      throw new StripeServiceError(
        `Unexpected error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'UNEXPECTED_ERROR',
        500,
        error
      );
    }
  }

  /**
   * NOUVELLE MÉTHODE : Test checkout sans code promo
   */
  async createCheckoutSessionWithoutPromo(request: Omit<CreateCheckoutSessionRequest, 'promoCode'>): Promise<CreateCheckoutSessionResponse> {
    console.log('🧪 [DEBUG] Testing checkout without promo code');
    return this.createCheckoutSession({
      ...request,
      promoCode: undefined
    });
  }

  /**
   * NOUVELLE MÉTHODE : Test avec code promo forcé
   */
  async createCheckoutSessionWithTestPromo(request: CreateCheckoutSessionRequest, testPromoCode: string): Promise<CreateCheckoutSessionResponse> {
    console.log('🧪 [DEBUG] Testing checkout with forced promo code:', testPromoCode);
    return this.createCheckoutSession({
      ...request,
      promoCode: testPromoCode
    });
  }

  /**
   * NOUVELLE MÉTHODE : Validation code promo côté client
   */
  async validatePromoCode(promoCode: string): Promise<{ valid: boolean; error?: string }> {
    console.log('🔍 [DEBUG] Validating promo code:', promoCode);
    
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session?.access_token) {
        return { valid: false, error: 'Authentication required' };
      }

      // Appel à une fonction Netlify de validation (à créer si nécessaire)
      const response = await fetch('/.netlify/functions/validate-promo-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ promo_code: promoCode }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.log('❌ [DEBUG] Promo validation failed:', errorData);
        return { valid: false, error: errorData.error || 'Invalid promo code' };
      }

      const data = await response.json();
      console.log('✅ [DEBUG] Promo validation success:', data);
      return { valid: true };

    } catch (error) {
      console.error('💥 [DEBUG] Promo validation error:', error);
      return { valid: false, error: 'Validation failed' };
    }
  }

  /**
   * Get subscription status for display
   */
  getSubscriptionStatusDisplay(status: string): { label: string; color: string } {
    const statusMap: Record<string, { label: string; color: string }> = {
      'active': { label: 'Actif', color: 'green' },
      'trialing': { label: 'Période d\'essai', color: 'blue' },
      'past_due': { label: 'Paiement en retard', color: 'orange' },
      'canceled': { label: 'Annulé', color: 'red' },
      'unpaid': { label: 'Impayé', color: 'red' },
      'incomplete': { label: 'Incomplet', color: 'yellow' },
      'incomplete_expired': { label: 'Expiré', color: 'red' },
      'paused': { label: 'En pause', color: 'gray' },
      'not_started': { label: 'Non démarré', color: 'gray' },
    };

    return statusMap[status] || { label: status, color: 'gray' };
  }

  /**
   * Format date for display
   */
  formatDate(timestamp: number): string {
    // Check if timestamp is in seconds (Stripe format) or milliseconds
    const date = timestamp > 9999999999 ? new Date(timestamp) : new Date(timestamp * 1000);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  /**
   * Get user's subscription data
   */
  async getUserSubscription(): Promise<any> {
    try {
     // Check if we have a valid authenticated user first
     const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
     
     if (authError || !authUser?.id) {
       console.warn('No authenticated user found for getUserSubscription:', authError?.message);
       return null;
     }
     
      // Récupérer les informations d'abonnement directement depuis la table profiles
      const { data, error } = await supabase
        .from('profiles')
        .select('subscription_id, subscription_status')
       .eq('id', authUser.id)
        .single();
      
      if (error) {
        console.error('Get subscription error:', error);
        return null;
      }

      // Transformer les données pour correspondre au format attendu
      if (data && data.subscription_id) {
        return {
          subscription_id: data.subscription_id,
          subscription_status: data.subscription_status,
          // Estimer la date de fin de période à un mois à partir d'aujourd'hui
          current_period_end: new Date().setMonth(new Date().getMonth() + 1),
          // Ces valeurs ne sont plus disponibles dans la table profiles
          cancel_at_period_end: false,
          payment_method_brand: null,
          payment_method_last4: null
        };
      }
      
      return null;
    } catch (error) {
      console.error('Get user subscription error:', error);
      return null;
    }
  }

  /**
   * Get user's payment methods
   */
  async getUserPaymentMethods(): Promise<any[]> {
    try {
      // In a real implementation, this would call Stripe API
      return [];
    } catch (error) {
      console.error('Get payment methods error:', error);
      return [];
    }
  }

  /**
   * Get user's invoices
   */
  async getUserInvoices(): Promise<any[]> {
    try {
      // In a real implementation, this would call Stripe API
      return [];
    } catch (error) {
      console.error('Get invoices error:', error);
      return [];
    }
  }

  /**
   * Redirect to Stripe Customer Portal
   */
  redirectToCustomerPortal(): void {
    // Redirect to Stripe Customer Portal
    window.open('https://billing.stripe.com/p/login/test_28o5nO9Oj8Oi9OM288', '_blank');
  }

  /**
   * Check if user has an active subscription
   */
  async hasActiveSubscription(): Promise<boolean> {
    try {
     // Check if we have a valid authenticated user first
     const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
     
     if (authError || !authUser?.id) {
       console.warn('No authenticated user found for hasActiveSubscription:', authError?.message);
       return false;
     }
     
      // Récupérer directement le statut d'abonnement depuis profiles
      const { data, error } = await supabase
        .from('profiles')
        .select('subscription_status')
       .eq('id', authUser.id)
        .single();
      
      if (error || !data || !data.subscription_status) {
        console.error('Check subscription status error:', error);
        return false;
      }

      const activeStatuses = ['active', 'trialing'];
      return activeStatuses.includes(data.subscription_status);
    } catch (error) {
      console.error('Check active subscription error:', error);
      return false;
    }
  }

  /**
   * Get user's credits or check if they have unlimited access
   */
  async getUserCreditsInfo(): Promise<{ hasCredits: boolean; isUnlimited: boolean; credits: number }> {
    try {
     console.log('🔍 [STRIPE_SERVICE] getUserCreditsInfo called');
     
     // First check if we have a valid authenticated user
     const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
     
     if (authError || !authUser?.id) {
       console.warn('No authenticated user found for getUserCreditsInfo, returning defaults:', authError?.message);
       return { hasCredits: false, isUnlimited: false, credits: 0 };
     }
     
     console.log('✅ Authenticated user found for credits check:', authUser.id);
     
      // Vérifier d'abord si l'utilisateur a un abonnement actif
      // Récupérer directement le statut d'abonnement et les crédits depuis profiles
      const { data, error } = await supabase
        .from('profiles')
        .select('subscription_status, credits')
       .eq('id', authUser.id)
        .single();
      
      if (error) {
       console.error('Get user credits info error for user', authUser.id, ':', error);
        console.error('🔍 [STRIPE_SERVICE] Supabase error details:', {
          code: error.code,
          message: error.message,
          details: error.details
        });
        return { hasCredits: false, isUnlimited: false, credits: 0 };
      }
      
     console.log('✅ Credits info retrieved:', { 
       userId: authUser.id, 
       credits: data?.credits, 
       subscriptionStatus: data?.subscription_status 
     });
      
      console.log('🔍 [STRIPE_SERVICE] Raw data from Supabase:', data);
     
      const activeStatuses = ['active', 'trialing'];
      const hasSubscription = data.subscription_status && activeStatuses.includes(data.subscription_status);
      
      console.log('🔍 [STRIPE_SERVICE] Subscription check:', {
        subscription_status: data.subscription_status,
        activeStatuses,
        hasSubscription
      });
      
      // Si l'utilisateur a un abonnement actif, il a un accès illimité
      if (hasSubscription) {
        console.log('✅ [STRIPE_SERVICE] User has unlimited access via subscription');
        return {
          hasCredits: true,
          isUnlimited: true,
          credits: 0 // La valeur n'est pas importante car isUnlimited est true
        };
      }
      
      // Sinon, vérifier les crédits disponibles
      const credits = data?.credits || 0;
      console.log('🔍 [STRIPE_SERVICE] User has credits-based access:', {
        credits,
        hasCredits: credits > 0
      });
      
      return {
        hasCredits: credits > 0,
        isUnlimited: false,
        credits
      };
    } catch (error) {
      console.error('Get user credits info error:', error);
      console.error('🔍 [STRIPE_SERVICE] Unexpected error in getUserCreditsInfo:', error);
      return { hasCredits: false, isUnlimited: false, credits: 0 };
    }
  }

  /**
   * Access Stripe Customer Portal
   */
  async accessCustomerPortal(): Promise<string> {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session?.access_token) {
        throw new StripeServiceError(
          'Authentication required',
          'AUTH_REQUIRED',
          401
        );
      }

      // Call Netlify function to get portal URL
      const response = await fetch('/.netlify/functions/payment-customer-portal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        let errorMessage = 'Failed to access customer portal';

        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (jsonError) {
          console.error('Failed to parse error response as JSON:', jsonError);
          const textError = await response.text();
          console.error('Response text:', textError);
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }

        throw new StripeServiceError(
          errorMessage,
          'CUSTOMER_PORTAL_ACCESS_FAILED',
          response.status
        );
      }

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Failed to parse success response as JSON:', jsonError);
        throw new StripeServiceError(
          'Invalid response from server',
          'INVALID_RESPONSE',
          500
        );
      }

      if (!data.portal_url) {
        throw new StripeServiceError(
          'No portal URL in response',
          'MISSING_PORTAL_URL',
          500
        );
      }

      return data.portal_url;
    } catch (error) {
      console.error('Customer portal access error:', error);
      throw error;
    }
  }

  async accessInvoicesPortal(): Promise<string> {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session?.access_token) {
        throw new StripeServiceError(
          'Authentication required',
          'AUTH_REQUIRED',
          401
        );
      }

      const response = await fetch('/.netlify/functions/payment-invoices-portal', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        let errorMessage = 'Failed to access invoices portal';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new StripeServiceError(
          errorMessage,
          'INVOICES_PORTAL_ACCESS_FAILED',
          response.status
        );
      }

      const data = await response.json();

      if (!data.portal_url) {
        throw new StripeServiceError(
          'No portal URL in response',
          'MISSING_PORTAL_URL',
          500
        );
      }

      return data.portal_url;
    } catch (error) {
      console.error('Invoices portal access error:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const stripeService = new StripeService();
export default stripeService;